import React, { FC, useReducer, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { RouteComponentProps } from 'react-router-dom';
import { useCommonStyles } from 'src/app/common/styles/common-styles';
import { useIntl } from 'react-intl';
import _ from 'lodash';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  LinearProgress,
  MenuItem,
  Select,
} from '@mui/material';
import { MANDATORY_FIELD_ERROR_TEXT } from 'src/app/common/constants';
import AsyncCsvLink from 'src/app/common/components/AsyncCsvLink';
import { useDispatch } from 'react-redux';
import { ErrorFieldType, useErrorHandler } from 'src/app/common/utils';
import { CREDIT_UPLOAD_TYPE } from 'src/app/modules/pruwallet/types/bulk-upload-types';
import { AlertType, appendAlertItem } from '@pruforce/common-adminweb-sdk';
import { getError, uploadFileToBlob } from 'src/app/modules/pruwallet/comm';
import { bulkUploadCredit, downloadBulkUploadCsv } from 'src/app/modules/pruwallet/network/bulkUpload';
import CircleUnchecked from '@mui/icons-material/RadioButtonUnchecked';
import CircleCheckedFilled from '@mui/icons-material/CheckCircle';

const useStyles = makeStyles()((theme) => ({
  container: {
    padding: 20,
    marginBottom: 20,
    borderRadius: 5,
    backgroundColor: theme.palette.common.white,
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 15,
  },
  rowContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  field: {
    fontSize: '1rem',
    marginRight: 10,
  },
  mandatory: {
    color: 'red',
  },
  template: {
    color: 'grey',
    fontSize: '14px',
  },
  fieldContainer: {
    width: FIELD_CONTAINER_WIDTH,
    boxSizing: 'border-box',
  },
  footerContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  errorText: {
    fontSize: 9,
    color: '#F018A6',
  },
  dateDivider: {
    marginLeft: 8,
    marginRight: 8,
  },
}));

const FIELD_CONTAINER_WIDTH = 220;

type UploadFormState = {
  uploadType: string;
  file: File | null;
  filename: string;
  requireNotification: boolean;
  blobId: string;
};

type ModifyFieldAction = {
  type: 'MODIFY_FIELD';
  payload: {
    field: keyof UploadFormState;
    value: any;
  };
};

type UploadFileAction = {
  type: 'UPLOAD_FILE';
  payload: {
    filename: string;
    file: File | null;
    blobId: string;
  };
};

type ResetAction = {
  type: 'RESET_STATE';
};
type UploadFormAction = ModifyFieldAction | UploadFileAction | ResetAction;

const initialState: UploadFormState = {
  uploadType: CREDIT_UPLOAD_TYPE,
  file: null,
  filename: '',
  requireNotification: false,
  blobId: '',
};

const formReducer = (state: UploadFormState, action: UploadFormAction): UploadFormState => {
  switch (action.type) {
    case 'MODIFY_FIELD':
      return {
        ...state,
        [action.payload.field]: action.payload.value,
      };
    case 'UPLOAD_FILE':
      return {
        ...state,
        file: action.payload.file,
        filename: action.payload.filename,
        blobId: action.payload.blobId,
      };
    case 'RESET_STATE':
      return initialState;
    default:
      return state;
  }
};

const BulkUploadCreditPage: FC<RouteComponentProps> = () => {
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const { classes: commonClasses } = useCommonStyles();
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });

  const [formState, formDispatch] = useReducer(formReducer, initialState);
  const [uploadFileProgress, setUploadFileProgress] = useState<number>();

  const { errorState, onSubmitErrorValidator } = useErrorHandler(formState, [
    {
      name: 'file',
      fieldType: ErrorFieldType.MANDATORY,
      condition: () => {
        return !formState.filename;
      },
    },
  ]);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const file = e.target.files[0];
    try {
      setUploadFileProgress(0);
      const createBlobRes = await uploadFileToBlob(file, setUploadFileProgress, dispatch);
      console.log('Blob upload response:', createBlobRes.blobId);
      formDispatch({
        type: 'UPLOAD_FILE',
        payload: {
          filename: file.name,
          file,
          blobId: createBlobRes.blobId,
        },
      });
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: 'Success',
            content: `File ${file.name} uploaded successfully`,
          },
        ]),
      );
    } catch (err) {
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.ERROR,
            title: 'Error',
            content: `${getError(err)?.message || err}`,
          },
        ]),
      );
    } finally {
      setUploadFileProgress(undefined);
    }
  };

  const onSubmit = async () => {
    const { hasError } = onSubmitErrorValidator();
    if (hasError) return;
    try {
      await bulkUploadCredit(formState.file, formState.filename, formState.requireNotification, formState.blobId);
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: 'Success',
            content: 'Bulk Upload successfully',
          },
        ]),
      );
      formDispatch({ type: 'RESET_STATE' });
    } catch (err) {
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.ERROR,
            title: 'Error',
            content: `${getError(err)?.message || err}`,
          },
        ]),
      );
    } finally {
      setUploadFileProgress(undefined);
    }
  };

  return (
    <>
      <div className={classes.container}>
        <div className={classes.headerContainer}>
          <div className={classes.rowContainer}>
            <div className={commonClasses.header}>{Translation(`title.wallet.bulkUpload.upload.credit`)}</div>
          </div>
        </div>
        <div>
          <div className={classes.rowContainer} style={{ marginBottom: 10 }}>
            <div className={classes.fieldContainer}>
              <span className={classes.field}>
                {Translation('pulseleads.bulkUpload.upload.selectBulkUploadType')}
                <span className={classes.mandatory}>*</span> :
              </span>
            </div>
            <div>
              <FormControl
                error={errorState.mandatory.uploadType}
                style={{ margin: 0 }}
                margin="dense"
                variant="outlined"
              >
                <Select
                  style={{ minWidth: 250 }}
                  value={formState.uploadType}
                  disabled
                  onChange={(e) => {
                    formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'uploadType', value: e.target.value } });
                  }}
                >
                  <MenuItem key={CREDIT_UPLOAD_TYPE} value={CREDIT_UPLOAD_TYPE}>
                    {Translation(`title.wallet.bulkUpload.upload.credit`)}
                  </MenuItem>
                </Select>
                {errorState.mandatory.uploadType && (
                  <FormHelperText className={classes.errorText}>{MANDATORY_FIELD_ERROR_TEXT}</FormHelperText>
                )}
              </FormControl>
            </div>
            <div className="col">
              <span>{Translation('wallet.bulkUpload.upload.bulkUploadTemplateCaption')}</span>
              <div>
                <span className={classes.template} style={{ marginTop: 10 }}>
                  {Translation('wallet.bulkUpload.common.template')}
                </span>
              </div>
              {/* Download template  start */}
              {formState.uploadType && (
                <AsyncCsvLink
                  isDisabled={false}
                  filename={`${formState.uploadType}.csv`}
                  dataParser={(str) => str}
                  asyncCall={() => downloadBulkUploadCsv(formState.uploadType, dispatch)}
                >
                  <Button style={{ padding: 1, fontSize: '14px' }}>
                    {Translation(`title.wallet.bulkUpload.upload.${formState.uploadType}`).toLocaleUpperCase()}
                  </Button>
                </AsyncCsvLink>
              )}
              {/* Download template  end */}
            </div>
          </div>
          <div className={classes.rowContainer}>
            <div className={classes.fieldContainer}>
              <span className={classes.field}>
                {Translation('pulseleads.bulkUpload.upload.bulkUploadWithTemplate')}
                <span className={classes.mandatory}>*</span> :
              </span>
            </div>
            <input
              id="upload-file"
              hidden
              type="file"
              accept=".csv"
              onClick={(e) => {
                const element = e.target as HTMLInputElement;
                element.value = '';
              }}
              onChange={(e) => handleFile(e)}
            />
            <div className={classes.rowContainer}>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => document.getElementById('upload-file')!.click()}
              >
                {Translation('app.button.chooseFile')}
              </Button>
              {formState.filename && <div style={{ marginLeft: 10 }}>{formState.filename}</div>}
              {errorState.mandatory.file && (
                <div style={{ marginLeft: 10 }} className={classes.errorText}>
                  {MANDATORY_FIELD_ERROR_TEXT}
                </div>
              )}
            </div>
            {!!uploadFileProgress && (
              <LinearProgress
                style={{ marginTop: 10 }}
                variant="determinate"
                color="secondary"
                value={uploadFileProgress}
              />
            )}
          </div>
          <div className={classes.rowContainer}>
            <div className={classes.fieldContainer}>
              <span className={classes.field}>{Translation('wallet.bulkUpload.upload.sendNotification')} :</span>
            </div>
            <FormControlLabel
              style={{ margin: '2px 12px 2px 0' }}
              control={
                <Checkbox
                  icon={<CircleUnchecked />}
                  checkedIcon={<CircleCheckedFilled />}
                  checked={formState.requireNotification === true}
                  onChange={(e) => {
                    if (e.target.checked) {
                      formDispatch({
                        type: 'MODIFY_FIELD',
                        payload: {
                          field: 'requireNotification',
                          value: true,
                        },
                      });
                    }
                  }}
                />
              }
              label={Translation('app.checkbox.yes')}
              labelPlacement="end"
            />
            <FormControlLabel
              style={{ margin: '2px 0' }}
              control={
                <Checkbox
                  icon={<CircleUnchecked />}
                  checkedIcon={<CircleCheckedFilled />}
                  checked={formState.requireNotification === false}
                  onChange={(e) => {
                    if (e.target.checked) {
                      formDispatch({
                        type: 'MODIFY_FIELD',
                        payload: {
                          field: 'requireNotification',
                          value: false,
                        },
                      });
                    }
                  }}
                />
              }
              label={Translation('app.checkbox.no')}
              labelPlacement="end"
            />
          </div>
          <div className={classes.footerContainer}>
            <Button variant="contained" color="secondary" onClick={onSubmit}>
              {Translation('app.button.submit')}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default BulkUploadCreditPage;
